import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import './i18n/i18n';
import { useTranslation } from 'react-i18next';
import PageToolbar from './util/PageToolbar';
import TabMenu from './util/TabMenu';
import CalendarPicker from './util/CalendarPicker';
import TabPanel from '@mui/lab/TabPanel';
import SearchFilter, { applySearchAndFilters, ActiveFilter } from './util/SearchFilter';
import { ColDef } from './util/DataTable';

/* ============================================================
   Tasks — Kanban board (portal design system)
   Данные хранятся в backend (task_card, task_card_assignees).
   Поля vis / repeat / repeatDate / done / cat — локальные (не персистируются).
   ============================================================ */

interface TasksPageProps {
  access: any;
}

interface ApiUser {
  id: number;
  first_name: string;
  last_name: string;
}

type Priority = 'high' | 'med' | 'low';

interface Task {
  id: number;
  title: string;
  desc: string;
  status: string;
  pr: Priority;
  who: string[];       // имена исполнителей (для отображения)
  whoIds: number[];    // userId исполнителей (для API)
  due: string;
  cat: string;         // локальное поле (не в backend)
  vis: string;         // локальное поле (не в backend)
  repeat: string;      // локальное поле (не в backend)
  repeatDate: string;  // локальное поле (не в backend)
  created: string;
  createdBy: string;
  done?: boolean;      // локальное поле (не в backend)
}

const AV = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#0891b2', '#dc2626', '#0d9488'];
const nameHash = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};
const personColor = (n: string) => AV[nameHash(n) % AV.length];
const initials = (n: string) =>
  n.trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

const userName = (u: { first_name?: string; last_name?: string }) =>
  [u.first_name, u.last_name].filter(Boolean).join(' ');

interface Column { key: string; label: string; color: string }
const BASE_COLS: Column[] = [
  { key: 'new', label: 'New', color: '#64748b' },
  { key: 'pending', label: 'Pending', color: '#d97706' },
  { key: 'progress', label: 'In Progress', color: '#2563eb' },
  { key: 'fulfilled', label: 'Done', color: '#059669' },
];
const COL_PALETTE = ['#64748b', '#d97706', '#2563eb', '#059669', '#0891b2', '#7c3aed', '#dc2626', '#0d9488', '#e11d48', '#0ea5e9'];

const PR_LABEL: Record<Priority, string> = { high: 'High', med: 'Medium', low: 'Low' };
const VIS: Record<string, string> = { all: 'Everyone', team: 'My team', assignees: 'Assignees only', private: 'Only me' };
const REPEAT: Record<string, string> = { none: 'None', daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', custom: 'On a date…' };
const CAT_COLOR: Record<string, string> = {
  Design: '#7c3aed', Backend: '#2563eb', HR: '#0d9488', Booking: '#0891b2',
  Finance: '#059669', i18n: '#d97706', Equipment: '#dc2626', Training: '#7c3aed', General: '#64748b',
};
const CATS = Object.keys(CAT_COLOR);

const fmtDateTime = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso
    : d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/* Иконки */
const IconEdit = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);
const IconX = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M18 6 6 18" /><path d="m6 6 12 12" />
  </svg>
);
const IconCheck = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

/* Маппинг API-ответа в локальный Task */
const mapApiTask = (card: any): Task => ({
  id: card.id,
  title: card.title ?? '',
  desc: card.description ?? '',
  status: card.status ?? 'new',
  pr: (card.priority as Priority) ?? 'med',
  who: (card.assignees ?? []).map((u: any) => userName(u)),
  whoIds: (card.assignees ?? []).map((u: any) => u.id as number),
  due: card.dueDate ?? '',
  cat: 'General',
  vis: 'all',
  repeat: 'none',
  repeatDate: '',
  created: card.createdAt ?? new Date().toISOString(),
  createdBy: card.createdByUser ? userName(card.createdByUser) : '',
});

const Avatar: React.FC<{ name: string; onRemove?: () => void }> = ({ name, onRemove }) => (
  <div
    className="tsk-ava"
    style={{ background: personColor(name), cursor: onRemove ? 'pointer' : undefined }}
    title={onRemove ? `${name} — remove` : name}
    onClick={onRemove}
  >
    {initials(name)}
  </div>
);

const autoGrow = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

const TasksPage: React.FC<TasksPageProps> = ({ access }) => {
  const { t } = useTranslation();
  const auth = useSelector((state: RootState) => state.auth.value);

  /* ---- Данные с сервера ---- */
  const [tasks, setTasks] = useState<Task[]>([]);
  const [people, setPeople] = useState<ApiUser[]>([]);
  const [me, setMe] = useState<ApiUser | null>(null);
  const [loading, setLoading] = useState(false);

  const ME_NAME = useMemo(() => (me ? userName(me) : ''), [me]);

  /* name → userId для отправки assigneeIds */
  const personIdMap = useMemo(
    () => Object.fromEntries(people.map(u => [userName(u), u.id])),
    [people],
  );

  /* ---- UI-состояние ---- */
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [scope, setScope] = useState('all');
  const [tab, setTab] = useState('board');
  const [columns, setColumns] = useState<Column[]>(BASE_COLS);
  const [openId, setOpenId] = useState<number | null>(null);
  const [mode, setMode] = useState<'read' | 'edit'>('read');
  const [editIsNew, setEditIsNew] = useState(false);
  const [draft, setDraft] = useState<Task | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [confirmDel, setConfirmDel] = useState<number | null>(null);
  const [colorEditKey, setColorEditKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const colMenuRef = useRef<HTMLDivElement>(null);
  const openCardRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ type: 'card'; id: number } | { type: 'col'; key: string } | null>(null);
  const tempSeq = useRef(-1); // отрицательные ID для несохранённых карточек

  const canManage = true;

  /* ---- Загрузка данных ---- */
  const fetchTasks = useCallback(async () => {
    try {
      const { data } = await axios.get('tasks');
      setTasks((data ?? []).map(mapApiTask));
    } catch {}
  }, []);

  const fetchMeta = useCallback(async () => {
    try {
      const [usersRes, meRes] = await Promise.all([
        axios.get('users'),
        axios.get('user'),
      ]);
      setPeople(usersRes.data ?? []);
      setMe(meRes.data ?? null);
    } catch {}
  }, []);

  useEffect(() => {
    if (!auth) return;
    setLoading(true);
    Promise.all([fetchTasks(), fetchMeta()]).finally(() => setLoading(false));
  }, [auth, fetchTasks, fetchMeta]);

  /* ---- Открытие / закрытие карточки ---- */
  const close = useCallback(() => {
    setOpenId(null); setMode('read'); setDraft(null); setEditIsNew(false); setMenuOpen(false); setConfirmDel(null);
  }, []);
  const openRead = useCallback((id: number) => {
    setOpenId(id); setMode('read'); setDraft(null); setMenuOpen(false); setConfirmDel(null);
  }, []);
  const openEdit = useCallback((task: Task) => {
    setOpenId(task.id); setMode('edit'); setDraft({ ...task, who: [...task.who], whoIds: [...task.whoIds] });
    setEditIsNew(false); setMenuOpen(false); setConfirmDel(null);
  }, []);

  const removeTask = useCallback(async (id: number) => {
    setTasks(prev => prev.filter(x => x.id !== id));
    setConfirmDel(null);
    if (openId === id) close();
    if (id > 0) {
      try { await axios.delete(`tasks/${id}`); } catch { fetchTasks(); }
    }
  }, [openId, close, fetchTasks]);

  const toggleDone = useCallback((id: number) => {
    setTasks(prev => prev.map(x => x.id === id ? { ...x, done: !x.done } : x));
  }, []);

  const addTask = useCallback((status: string) => {
    const id = tempSeq.current--;
    const task: Task = {
      id, title: '', desc: '', status, pr: 'med',
      who: ME_NAME ? [ME_NAME] : [],
      whoIds: me ? [me.id] : [],
      due: '', cat: 'General', vis: 'all', repeat: 'none', repeatDate: '',
      created: new Date().toISOString(), createdBy: ME_NAME,
    };
    setTasks(prev => {
      const idx = prev.findIndex(x => x.status === status);
      const next = [...prev];
      if (idx > -1) next.splice(idx, 0, task); else next.push(task);
      return next;
    });
    setOpenId(task.id); setMode('edit'); setDraft({ ...task, who: [...task.who], whoIds: [...task.whoIds] });
    setEditIsNew(true); setMenuOpen(false); setConfirmDel(null);
  }, [ME_NAME, me]);

  const saveEdit = useCallback(async () => {
    if (!draft) { close(); return; }
    const clean: Task = { ...draft, title: draft.title.trim(), desc: draft.desc.trim() };
    if (editIsNew && !clean.title && !clean.desc) { removeTask(clean.id); return; }

    const body = {
      title: clean.title || 'Untitled',
      description: clean.desc || null,
      status: clean.status,
      priority: clean.pr,
      dueDate: clean.due || null,
      assigneeIds: clean.who
        .map(n => personIdMap[n])
        .filter((v): v is number => typeof v === 'number'),
    };

    if (editIsNew) {
      try {
        const { data } = await axios.post('tasks', body);
        const created = mapApiTask(data);
        setTasks(prev => prev.map(x => x.id === clean.id ? created : x));
        setOpenId(created.id);
      } catch {
        setTasks(prev => prev.map(x => x.id === clean.id ? clean : x));
      }
    } else {
      setTasks(prev => prev.map(x => x.id === clean.id ? clean : x));
      try {
        const { data } = await axios.put(`tasks/${clean.id}`, body);
        setTasks(prev => prev.map(x => x.id === clean.id ? mapApiTask(data) : x));
      } catch { fetchTasks(); }
    }
    setDraft(null); setEditIsNew(false); setMode('read');
  }, [draft, editIsNew, removeTask, close, personIdMap, fetchTasks]);

  const cancelEdit = useCallback(() => {
    if (editIsNew && draft) { removeTask(draft.id); return; }
    setDraft(null); setEditIsNew(false); setMode('read');
  }, [editIsNew, draft, removeTask]);

  const toggleAssignee = useCallback((userId: number, name: string, removeOnly = false) => {
    setDraft(d => {
      if (!d) return d;
      const has = d.whoIds.includes(userId);
      if (has) {
        return { ...d, who: d.who.filter(n => n !== name), whoIds: d.whoIds.filter(i => i !== userId) };
      }
      if (removeOnly) return d;
      return { ...d, who: [...d.who, name], whoIds: [...d.whoIds, userId] };
    });
  }, []);

  /* ---- Колонки: добавить / удалить / цвет ---- */
  const addColumn = useCallback(() => {
    const label = newColName.trim();
    if (!label) return;
    setColumns(prev => {
      const base = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'col';
      let key = base; let n = 1;
      while (prev.some(c => c.key === key)) key = `${base}-${++n}`;
      const color = COL_PALETTE[prev.length % COL_PALETTE.length];
      return [...prev, { key, label, color }];
    });
    setNewColName('');
  }, [newColName]);

  const deleteColumn = useCallback((key: string) => {
    if (columns.length <= 1) return;
    const idx = columns.findIndex(c => c.key === key);
    if (idx < 0) return;
    const neighbor = (columns[idx - 1] || columns[idx + 1])?.key;
    const hasCards = tasks.some(tk => tk.status === key);
    if (hasCards && !window.confirm(t('DeleteColumnConfirm') as string)) return;
    if (neighbor) {
      const movers = tasks.filter(tk => tk.status === key && tk.id > 0);
      setTasks(prev => prev.map(tk => tk.status === key ? { ...tk, status: neighbor } : tk));
      movers.forEach(tk => axios.put(`tasks/${tk.id}`, { status: neighbor }).catch(() => {}));
    }
    setColumns(prev => prev.filter(c => c.key !== key));
    setColorEditKey(null);
  }, [columns, tasks, t]);

  const setColumnColor = useCallback((key: string, color: string) => {
    setColumns(prev => prev.map(c => c.key === key ? { ...c, color } : c));
    setColorEditKey(null);
  }, []);

  /* ---- Drag & drop: карточки и колонки ---- */
  const onCardDragStart = (e: React.DragEvent, id: number) => {
    if (mode === 'edit' && openId === id) { e.preventDefault(); return; }
    e.stopPropagation();
    drag.current = { type: 'card', id };
    e.dataTransfer.effectAllowed = 'move';
  };
  const onColDragStart = (e: React.DragEvent, key: string) => {
    drag.current = { type: 'col', key };
    e.dataTransfer.effectAllowed = 'move';
  };

  const onCardDrop = (e: React.DragEvent, targetId: number) => {
    const d = drag.current;
    if (!d || d.type !== 'card') return;
    e.preventDefault();
    e.stopPropagation();
    drag.current = null;
    setDragOverKey(null);
    if (d.id === targetId) return;
    let newStatus = '';
    setTasks(prev => {
      const from = prev.findIndex(x => x.id === d.id);
      const target = prev.find(x => x.id === targetId);
      if (from < 0 || !target) return prev;
      newStatus = target.status;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      moved.status = target.status;
      const to = next.findIndex(x => x.id === targetId);
      next.splice(to, 0, moved);
      return next;
    });
    if (d.id > 0 && newStatus) {
      axios.put(`tasks/${d.id}`, { status: newStatus }).catch(() => fetchTasks());
    }
  };

  const onColDrop = (e: React.DragEvent, status: string) => {
    e.preventDefault();
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (d.type === 'card') {
      const movedId = d.id;
      setTasks(prev => {
        const idx = prev.findIndex(x => x.id === d.id);
        if (idx < 0) return prev;
        const next = [...prev];
        const [moved] = next.splice(idx, 1);
        moved.status = status;
        let last = -1;
        next.forEach((x, i) => { if (x.status === status) last = i; });
        next.splice(last + 1, 0, moved);
        return next;
      });
      if (movedId > 0) {
        axios.put(`tasks/${movedId}`, { status }).catch(() => fetchTasks());
      }
    } else {
      setColumns(prev => {
        const fromI = prev.findIndex(c => c.key === d.key);
        const toI = prev.findIndex(c => c.key === status);
        if (fromI < 0 || toI < 0 || fromI === toI) return prev;
        const next = [...prev];
        const [moved] = next.splice(fromI, 1);
        next.splice(toI, 0, moved);
        return next;
      });
    }
  };

  /* ---- Колонки-фильтры для SearchFilter ---- */
  const taskColumns = useMemo<ColDef<Task>[]>(() => [
    {
      key: 'cat', label: t('Category'), filterType: 'select',
      filterOptions: CATS.map(c => ({ value: c, label: c })),
      filterExtract: r => r.cat, render: () => null,
    },
    {
      key: 'pr', label: t('Priority'), filterType: 'select',
      filterOptions: (Object.keys(PR_LABEL) as Priority[]).map(p => ({ value: p, label: PR_LABEL[p] })),
      filterExtract: r => r.pr, render: () => null,
    },
  ], [t]);

  const filtered = useMemo(() => {
    const base = applySearchAndFilters(tasks, query, filters, taskColumns,
      r => [r.title, r.desc, r.cat, ...r.who].join(' '));
    if (scope === 'all') return base;
    return base.filter(tk =>
      scope === 'created' ? tk.createdBy === ME_NAME
        : scope === 'assigned' ? tk.who.includes(ME_NAME)
          : tk.vis === 'all');
  }, [tasks, query, filters, taskColumns, scope, ME_NAME]);

  /* ---- Клавиатура и клик вне ---- */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || openId == null) return;
      if (mode === 'edit') cancelEdit(); else close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openId, mode, cancelEdit, close]);

  useEffect(() => {
    if (openId == null) return;
    const onDown = (e: MouseEvent) => {
      if (openCardRef.current && !openCardRef.current.contains(e.target as Node)) {
        if (mode === 'edit') cancelEdit(); else close();
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openId, mode, cancelEdit, close]);

  useEffect(() => {
    if (!colMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target as Node)) { setColMenuOpen(false); setColorEditKey(null); }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [colMenuOpen]);

  /* ---- Рендеры ---- */
  const toolButtons = (task: Task, editing: boolean) => (
    <>
      <button
        type="button"
        className={`tsk-tool ${editing ? 'on' : ''}`}
        title={t('Edit')}
        onClick={e => { e.stopPropagation(); if (editing) cancelEdit(); else openEdit(task); }}
      >
        <IconEdit />
      </button>
      <button
        type="button"
        className="tsk-tool danger"
        title={t('Delete')}
        onClick={e => { e.stopPropagation(); setConfirmDel(task.id); }}
      >
        <IconX />
      </button>
    </>
  );

  const doneToggle = (task: Task, onToggle: () => void) => (
    <button
      type="button"
      className={`tsk-done ${task.done ? 'on' : ''}`}
      title={(task.done ? t('Mark as not done') : t('Mark as done')) as string}
      onClick={e => { e.stopPropagation(); onToggle(); }}
    >
      {task.done && <IconCheck />}
    </button>
  );

  const confirmOverlay = (task: Task) => (
    confirmDel === task.id ? (
      <div className="tsk-confirm" onClick={e => e.stopPropagation()}>
        <div className="tsk-confirm-box">
          <div className="tsk-confirm-title">{t('Delete this card?')}</div>
          <div className="tsk-confirm-actions">
            <button type="button" className="dt-btn-ghost" onClick={() => setConfirmDel(null)}>{t('Cancel')}</button>
            <button type="button" className="tsk-btn-danger" onClick={() => removeTask(task.id)}>{t('Delete')}</button>
          </div>
        </div>
      </div>
    ) : null
  );

  const assigneeMenu = () => {
    if (!draft) return null;
    const selected = people.filter(u => draft.whoIds.includes(u.id));
    const available = people.filter(u => !draft.whoIds.includes(u.id));
    const row = (u: ApiUser) => {
      const name = userName(u);
      const isSel = draft.whoIds.includes(u.id);
      return (
        <div key={u.id} className={`tsk-assign-opt ${isSel ? 'sel' : ''}`} onClick={() => toggleAssignee(u.id, name)}>
          <div className="tsk-ava" style={{ background: personColor(name), boxShadow: 'none' }}>{initials(name)}</div>
          <span className="tsk-assign-name">{name}</span>
          <span className="tsk-check">✓</span>
        </div>
      );
    };
    return (
      <div className="tsk-assign-menu">
        {selected.length > 0 && <div className="tsk-assign-group">{t('Selected')}</div>}
        {selected.map(row)}
        {available.length > 0 && <div className="tsk-assign-group">{t('Available')}</div>}
        {available.map(row)}
      </div>
    );
  };

  const renderCard = (task: Task) => {
    const isOpen = openId === task.id;

    if (isOpen && mode === 'edit' && draft) {
      return (
        <div className={`tsk-card tsk-editing ${draft.done ? 'tsk-done-card' : ''}`} key={task.id} ref={openCardRef}>
          <div className="tsk-tools-row">
            {doneToggle(draft, () => setDraft({ ...draft, done: !draft.done }))}
            <div className="tsk-spacer" />
            {toolButtons(task, true)}
          </div>
          <textarea
            className="tsk-edit-title"
            rows={1}
            autoFocus
            ref={autoGrow}
            placeholder={t('Task title…')}
            value={draft.title}
            onChange={e => { setDraft({ ...draft, title: e.target.value }); autoGrow(e.target); }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(); } }}
          />
          <textarea
            className="tsk-edit-desc"
            ref={autoGrow}
            placeholder={t('Description…')}
            value={draft.desc}
            onChange={e => { setDraft({ ...draft, desc: e.target.value }); autoGrow(e.target); }}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveEdit(); } }}
          />

          <div className="tsk-edit-grid">
            <div className="tsk-edit-field">
              <label>{t('Priority')}</label>
              <select value={draft.pr} onChange={e => setDraft({ ...draft, pr: e.target.value as Priority })}>
                {(Object.keys(PR_LABEL) as Priority[]).map(p => <option key={p} value={p}>{PR_LABEL[p]}</option>)}
              </select>
            </div>
            <div className="tsk-edit-field">
              <label>{t('Category')}</label>
              <select value={draft.cat} onChange={e => setDraft({ ...draft, cat: e.target.value })}>
                {CATS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="tsk-edit-field">
              <label>{t('Who can see it')}</label>
              <select value={draft.vis} onChange={e => setDraft({ ...draft, vis: e.target.value })}>
                {Object.keys(VIS).map(v => <option key={v} value={v}>{t(VIS[v])}</option>)}
              </select>
            </div>
            <div className="tsk-edit-field">
              <label>{t('Deadline')}</label>
              <CalendarPicker mode="single" variant="field" output="string" value={draft.due} onChange={(v) => setDraft({ ...draft, due: v })} />
            </div>
          </div>

          <div className="tsk-edit-row2">
            <div className="tsk-edit-field tsk-f-repeat">
              <label>{t('Repeat')}</label>
              <select value={draft.repeat} onChange={e => setDraft({ ...draft, repeat: e.target.value })}>
                {Object.keys(REPEAT).map(r => <option key={r} value={r}>{t(REPEAT[r])}</option>)}
              </select>
              {draft.repeat === 'custom' && (
                <CalendarPicker mode="single" variant="field" output="string" value={draft.repeatDate} onChange={(v) => setDraft({ ...draft, repeatDate: v })} />
              )}
            </div>
            <div className="tsk-edit-field tsk-f-assign">
              <label>{t('Assigned')}</label>
              <div className="tsk-assign">
                <div className="tsk-assign-selected">
                  {draft.who.map((n, i) => (
                    <Avatar key={draft.whoIds[i] ?? n} name={n}
                      onRemove={() => toggleAssignee(draft.whoIds[i], n, true)} />
                  ))}
                </div>
                <button type="button" className="tsk-assign-add" title={t('Add people')} onClick={() => setMenuOpen(v => !v)}>+</button>
                {menuOpen && assigneeMenu()}
              </div>
            </div>
          </div>

          <div className="tsk-edit-field">
            <label>{t('Created')}</label>
            <div className="tsk-edit-static">{fmtDateTime(draft.created)}{draft.createdBy ? ` · ${draft.createdBy}` : ''}</div>
          </div>

          <div className="tsk-edit-actions">
            <div className="tsk-spacer" />
            <button type="button" className="dt-btn-ghost" onClick={cancelEdit}>{t('Cancel')}</button>
            <button type="button" className="btn-primary" onClick={saveEdit}>{t('Save')}</button>
          </div>
          {confirmOverlay(task)}
        </div>
      );
    }

    const showDesc = isOpen && mode === 'read';
    return (
      <div
        className={`tsk-card ${showDesc ? 'tsk-open' : ''} ${task.done ? 'tsk-done-card' : ''}`}
        key={task.id}
        ref={isOpen ? openCardRef : undefined}
        draggable
        onDragStart={e => onCardDragStart(e, task.id)}
        onDrop={e => onCardDrop(e, task.id)}
        onClick={() => { if (!canManage || confirmDel === task.id) return; if (showDesc) close(); else openRead(task.id); }}
      >
        {canManage && <div className="tsk-card-tools">{toolButtons(task, false)}</div>}
        <div className="tsk-title-row">
          {doneToggle(task, () => toggleDone(task.id))}
          <div className="tsk-title">{task.title || <span style={{ opacity: 0.5 }}>{t('Untitled')}</span>}</div>
        </div>
        {showDesc && task.desc && <div className="tsk-desc">{task.desc}</div>}
        <div className="tsk-foot">
          <div className="tsk-ava-stack">{task.who.map(n => <Avatar key={n} name={n} />)}</div>
          <span className="tsk-meta">
            <span className={`tsk-dot p-${task.pr}`} />
            <span>{PR_LABEL[task.pr]}</span>
          </span>
        </div>
        {confirmOverlay(task)}
      </div>
    );
  };

  return (
    <div
      className="tasks-page"
      style={{ display: 'flex', flexDirection: 'column', height: 'var(--page-height)', overflow: 'hidden' }}
    >
      <style>{TASKS_CSS}</style>

      <TabMenu
        value={tab}
        onChange={setTab}
        tabs={[{ label: t('TasksNav'), value: 'board' }]}
      >
        <TabPanel
          value="board"
          sx={{ padding: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          <PageToolbar>
            {canManage && (
              <button type="button" className="btn-primary" onClick={() => addTask(columns[0]?.key ?? 'new')}>+ {t('Add task')}</button>
            )}
            <SearchFilter
              grow
              columns={taskColumns}
              search={query}
              onSearch={setQuery}
              filters={filters}
              onFilters={setFilters}
              placeholder={`${t('Search tasks')}...`}
            />
            <select className="tsk-scope" value={scope} onChange={e => setScope(e.target.value)} title={t('Filter')}>
              <option value="all">{t('All')}</option>
              <option value="created">{t('Created by me')}</option>
              <option value="assigned">{t('Assigned to me')}</option>
              <option value="visible">{t('Visible to all')}</option>
            </select>
            {canManage && (
              <div className="tsk-boardmenu" ref={colMenuRef}>
                <button type="button" className="tsk-dots" title={t('Board settings')} aria-label={t('Board settings')} onClick={() => setColMenuOpen(v => !v)}>⋮</button>
                {colMenuOpen && (
                  <div className="tsk-menu">
                    <div className="tsk-menu-label">{t('Columns')}</div>
                    {columns.map(c => (
                      <React.Fragment key={c.key}>
                        <div className="tsk-menu-item">
                          <button
                            type="button"
                            className="tsk-menu-dot"
                            style={{ background: c.color }}
                            title={t('Change color') as string}
                            onClick={() => setColorEditKey(k => k === c.key ? null : c.key)}
                          />
                          <span className="tsk-menu-name">{c.label}</span>
                          <span className="tsk-menu-count">{tasks.filter(tk => tk.status === c.key).length}</span>
                          <button
                            type="button"
                            className="tsk-menu-del"
                            disabled={columns.length <= 1}
                            title={t('Delete')}
                            onClick={() => deleteColumn(c.key)}
                          >
                            <IconX />
                          </button>
                        </div>
                        {colorEditKey === c.key && (
                          <div className="tsk-swatches">
                            {COL_PALETTE.map(col => (
                              <button
                                key={col}
                                type="button"
                                aria-label={col}
                                className={`tsk-swatch ${col === c.color ? 'on' : ''}`}
                                style={{ background: col }}
                                onClick={() => setColumnColor(c.key, col)}
                              />
                            ))}
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                    <div className="tsk-menu-sep" />
                    <div className="tsk-menu-add">
                      <input
                        value={newColName}
                        onChange={e => setNewColName(e.target.value)}
                        placeholder={t('New column name…')}
                        maxLength={24}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addColumn(); } }}
                      />
                      <button type="button" onClick={addColumn}>{t('Add')}</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </PageToolbar>

          {loading ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, color: 'var(--text-color-calendar-week)' }}>
              {t('Loading…')}
            </div>
          ) : (
            <div className="tsk-board-scroll">
              <div
                className="tsk-board"
                style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
              >
                {columns.map(col => {
                  const items = filtered.filter(x => x.status === col.key);
                  return (
                    <div
                      className={`tsk-col ${dragOverKey === col.key ? 'tsk-drag-over' : ''}`}
                      key={col.key}
                      onDragOver={e => { e.preventDefault(); if (dragOverKey !== col.key) setDragOverKey(col.key); }}
                      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverKey(k => k === col.key ? null : k); }}
                      onDrop={e => { setDragOverKey(null); onColDrop(e, col.key); }}
                    >
                      <div
                        className="tsk-col-head"
                        style={{ ['--col-c' as any]: col.color }}
                        draggable={canManage}
                        onDragStart={e => onColDragStart(e, col.key)}
                        title={t('Drag to reorder') as string}
                      >
                        {canManage && (
                          <button type="button" className="tsk-col-add-ic" title={t('Add card')} onClick={() => addTask(col.key)}>+</button>
                        )}
                        <span className="tsk-col-title">{col.label}</span>
                        <span className="tsk-count">{items.length}</span>
                      </div>
                      {items.map(renderCard)}
                      <div className={`tsk-col-drop ${items.length ? '' : 'empty'}`}>
                        {items.length ? '' : t('Drop tasks here')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </TabPanel>
      </TabMenu>
    </div>
  );
};

/* Scoped styles */
const TASKS_CSS = `
.tsk-board-scroll { flex:1; overflow:auto; min-height:0; }

.tsk-board { display:grid; grid-template-columns:repeat(4,1fr); gap:var(--space-l3); align-items:stretch; padding-bottom:24px; }
.tsk-col { display:flex; flex-direction:column; gap:var(--space-l3); min-height:240px; border-radius:var(--radius-card); transition:background .15s; }
.tsk-col.tsk-drag-over { background:color-mix(in srgb, var(--color-info) 8%, transparent); }
.tsk-col-drop { flex:1; min-height:70px; border-radius:var(--radius-card); display:grid; place-items:center;
  font-size:12px; font-weight:600; color:var(--text-color-calendar-week);
  border:2px dashed transparent; transition:border-color .15s, color .15s; }
.tsk-col-drop.empty { border-color:var(--border-input); }
.tsk-col.tsk-drag-over .tsk-col-drop { border-color:var(--color-info); color:var(--color-info); }
.tsk-col-head { --col-c:#64748b; display:flex; align-items:center; gap:6px; padding:6px 8px; border-radius:var(--radius-card);
  background:color-mix(in srgb, var(--col-c) 14%, transparent);
  border:1px solid color-mix(in srgb, var(--col-c) 28%, transparent);
  backdrop-filter:blur(10px); -webkit-backdrop-filter:blur(10px);
  color:color-mix(in srgb, var(--col-c) 88%, var(--text-color));
  font-size:12px; font-weight:800; text-transform:uppercase; letter-spacing:.04em; cursor:grab; }
.tsk-col-head:active { cursor:grabbing; }
.tsk-col-add-ic { flex:none; width:22px; height:22px; border:none; border-radius:var(--radius-input);
  background:color-mix(in srgb, var(--col-c) 34%, transparent); color:color-mix(in srgb, var(--col-c) 96%, var(--text-color));
  font-size:16px; font-weight:900; line-height:1; cursor:pointer; display:grid; place-items:center;
  transition:background .15s, transform .15s; }
.tsk-col-add-ic:hover { background:color-mix(in srgb, var(--col-c) 48%, transparent); transform:scale(1.08); }
.tsk-col-title { flex:1; text-align:left; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.tsk-col-head .tsk-count { flex:none; margin-right:2px; background:color-mix(in srgb, var(--col-c) 30%, transparent);
  color:color-mix(in srgb, var(--col-c) 96%, var(--text-color)); border-radius:999px; padding:1px 8px; font-weight:900; }
.dark-theme .tsk-col-head { background:color-mix(in srgb, var(--col-c) 24%, transparent);
  color:color-mix(in srgb, var(--col-c) 70%, var(--text-color)); }

.tsk-card { position:relative; background:var(--bg-form); border:1px solid var(--border-new); border-radius:var(--radius-card);
  padding:10px 12px; display:flex; flex-direction:column; gap:7px; cursor:pointer; box-shadow:0 2px 10px var(--shadow-new);
  transition:transform .15s, box-shadow .25s, border-color .2s; }
.tsk-card:hover { transform:translateY(-3px); box-shadow:0 10px 28px color-mix(in srgb, var(--color-info) 20%, transparent); border-color:color-mix(in srgb, var(--color-info) 35%, transparent); }
.tsk-card-tools { position:absolute; top:7px; right:7px; display:flex; gap:4px; z-index:2; }
.tsk-tool { width:24px; height:24px; border:none; border-radius:var(--radius-input); background:var(--btn-user-form);
  color:var(--text-color-calendar-week); cursor:pointer; display:grid; place-items:center;
  transition:background .15s, color .15s; }
.tsk-tool svg { display:block; }
.tsk-tool:hover { background:var(--btn-user-hover); color:var(--text-color); }
.tsk-tool.on { background:var(--color-info-bg); color:var(--color-info); }
.tsk-tool.danger:hover { background:var(--color-danger-bg); color:var(--color-danger); }
.tsk-title-row { display:flex; align-items:flex-start; gap:9px; padding-right:56px; }
.tsk-title { flex:1; font-size:14px; font-weight:700; line-height:1.3; color:var(--text-color); word-break:break-word; }
.tsk-done { flex:none; width:20px; height:20px; margin-top:1px; border-radius:50%; padding:0;
  border:2px solid var(--text-color-calendar-week); background:transparent; color:#fff; cursor:pointer;
  display:grid; place-items:center; transition:background .15s, border-color .15s, transform .12s; }
.tsk-done:hover { border-color:var(--color-success); background:var(--color-success-bg); transform:scale(1.12); }
.tsk-done.on { background:var(--color-success); border-color:var(--color-success); }
.tsk-done.on:hover { background:var(--color-success); }
.tsk-done svg { display:block; }
.tsk-done-card { opacity:.72; }
.tsk-desc { font-size:12px; color:var(--text-color-calendar); line-height:1.45; white-space:pre-wrap; word-break:break-word; }
.tsk-foot { display:flex; align-items:center; justify-content:space-between; }
.tsk-meta { display:inline-flex; align-items:center; gap:6px; font-size:11px; color:var(--text-color-calendar-week); font-weight:600; }
.tsk-dot { width:8px; height:8px; border-radius:50%; flex:none; }
.tsk-dot.p-high{background:#dc2626;} .tsk-dot.p-med{background:#d97706;} .tsk-dot.p-low{background:#059669;}

.tsk-ava { width:22px; height:22px; border-radius:50%; display:grid; place-items:center; font-size:10px; font-weight:800;
  color:#fff; flex:none; box-shadow:0 0 0 2px var(--bg-form); }
.tsk-ava-stack { display:flex; }
.tsk-ava-stack .tsk-ava { margin-left:-7px; }
.tsk-ava-stack .tsk-ava:first-child { margin-left:0; }

.tsk-editing { cursor:default; gap:7px; }
.tsk-editing:hover { transform:none; box-shadow:0 2px 10px var(--shadow-new); border-color:var(--border-new); }
.tsk-tools-row { display:flex; justify-content:flex-end; gap:4px; }
.tsk-edit-title, .tsk-edit-desc, .tsk-edit-field input, .tsk-edit-field select {
  width:100%; min-width:0; box-sizing:border-box; background:var(--bg-input); color:var(--text-color);
  border:1px solid var(--border-input); border-radius:var(--radius-input); padding:6px 8px; font-size:13px; outline:none; font-family:inherit; }
.tsk-edit-title { font-weight:800; font-size:15px; line-height:1.25; resize:none; overflow:hidden; white-space:pre-wrap; word-break:break-word; }
.tsk-edit-desc { resize:none; overflow:hidden; min-height:56px; line-height:1.4; white-space:pre-wrap; word-break:break-word; }
.tsk-edit-title:focus, .tsk-edit-desc:focus, .tsk-edit-field input:focus, .tsk-edit-field select:focus {
  border-color:var(--color-info); box-shadow:0 0 0 3px var(--color-info-bg); }
.tsk-edit-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(110px, 1fr)); gap:8px; }
.tsk-edit-row2 { display:flex; gap:8px; align-items:flex-start; flex-wrap:wrap; }
.tsk-f-repeat { flex:0 0 128px; }
.tsk-f-assign { flex:1 1 150px; min-width:0; }
.tsk-edit-field { display:flex; flex-direction:column; gap:3px; min-width:0; }
.tsk-edit-field label { font-size:9.5px; font-weight:700; text-transform:uppercase; letter-spacing:.03em; color:var(--text-color-calendar-week); line-height:1.2; }
.tsk-edit-static { font-size:11px; color:var(--text-color-calendar); padding:6px 8px; background:var(--btn-user-form); border-radius:var(--radius-input); }
.tsk-edit-actions { display:flex; align-items:center; gap:var(--space-l3); margin-top:2px; }
.tsk-spacer { flex:1; }
.tsk-btn-danger { border:none; background:var(--color-danger); color:#fff; padding:7px 14px; border-radius:var(--radius-input); font-size:12px; font-weight:700; cursor:pointer; }
.tsk-btn-danger:hover { background:color-mix(in srgb, var(--color-danger) 85%, #000); }

.tsk-confirm { position:absolute; inset:0; z-index:5; display:flex; align-items:center; justify-content:center;
  background:color-mix(in srgb, var(--bg-form) 62%, transparent); backdrop-filter:blur(2px); border-radius:var(--radius-card); }
.tsk-confirm-box { background:var(--bg-form); border:1px solid var(--border-input); border-radius:var(--radius-card);
  box-shadow:0 10px 28px var(--shadow-new); padding:12px; display:flex; flex-direction:column; gap:10px; max-width:220px; }
.tsk-confirm-title { font-size:13px; font-weight:700; color:var(--text-color); text-align:center; }
.tsk-confirm-actions { display:flex; gap:8px; justify-content:center; }

.tsk-assign { position:relative; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.tsk-assign-selected { display:flex; }
.tsk-assign-selected .tsk-ava { margin-left:-7px; transition:transform .12s; }
.tsk-assign-selected .tsk-ava:first-child { margin-left:0; }
.tsk-assign-selected .tsk-ava:hover { transform:translateY(-2px); outline:2px solid var(--color-danger); outline-offset:1px; }
.tsk-assign-add { width:22px; height:22px; flex:none; border-radius:50%; border:1px dashed var(--border-input);
  background:var(--btn-user-form); color:var(--text-color-calendar-week); font-size:14px; line-height:1; cursor:pointer;
  display:grid; place-items:center; transition:color .15s, border-color .15s; }
.tsk-assign-add:hover { color:var(--color-info); border-color:var(--color-info); }
.tsk-assign-menu { position:absolute; top:calc(100% + 6px); left:0; z-index:var(--z-dropdown); min-width:200px; background:var(--bg-form);
  border:1px solid var(--border-input); border-radius:var(--radius-card); box-shadow:0 10px 28px var(--shadow-new); padding:6px;
  display:flex; flex-direction:column; gap:2px; max-height:240px; overflow:auto; }
.tsk-assign-group { font-size:9.5px; font-weight:800; text-transform:uppercase; letter-spacing:.05em; color:var(--text-color-calendar-week); padding:5px 8px 2px; }
.tsk-assign-opt { display:flex; align-items:center; gap:9px; padding:6px 8px; border-radius:var(--radius-input); cursor:pointer;
  font-size:13px; font-weight:600; color:var(--text-color); }
.tsk-assign-opt:hover { background:var(--btn-user-form); }
.tsk-assign-name { flex:1; }
.tsk-check { color:var(--color-info); font-weight:800; opacity:0; }
.tsk-assign-opt.sel .tsk-check { opacity:1; }

.tsk-scope { background:var(--bg-input); border:1px solid var(--border-input); color:var(--text-color);
  border-radius:var(--radius-input); padding:8px 10px; font-size:13px; cursor:pointer; outline:none; }

.tsk-boardmenu { position:relative; flex:none; }
.tsk-dots { width:34px; height:34px; border:1px solid var(--border-input); background:var(--btn-user-form); color:var(--text-color);
  border-radius:var(--radius-input); cursor:pointer; font-size:16px; line-height:1; }
.tsk-dots:hover { background:var(--btn-user-hover); }
.tsk-menu { position:absolute; top:calc(100% + 6px); right:0; z-index:var(--z-modal); min-width:270px; background:var(--bg-form);
  border:1px solid var(--border-input); border-radius:var(--radius-card); box-shadow:0 16px 40px rgba(0,0,0,.22); padding:8px; }
.tsk-menu-label { font-size:10px; font-weight:800; text-transform:uppercase; letter-spacing:.06em; color:var(--text-color-calendar-week); padding:4px 8px; }
.tsk-menu-item { display:flex; align-items:center; gap:9px; padding:6px 8px; border-radius:var(--radius-input); }
.tsk-menu-item:hover { background:var(--btn-user-form); }
.tsk-menu-dot { width:14px; height:14px; border-radius:50%; flex:none; border:2px solid var(--bg-form); box-shadow:0 0 0 1px var(--border-input); cursor:pointer; padding:0; }
.tsk-menu-dot:hover { transform:scale(1.12); }
.tsk-menu-name { flex:1; font-size:13px; font-weight:600; color:var(--text-color); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.tsk-menu-count { font-size:11px; color:var(--text-color-calendar-week); font-weight:700; }
.tsk-menu-del { border:none; background:none; cursor:pointer; color:var(--text-color-calendar-week); opacity:.7; display:grid; place-items:center; padding:2px; border-radius:6px; }
.tsk-menu-del:hover:not(:disabled) { opacity:1; color:var(--color-danger); background:var(--color-danger-bg); }
.tsk-menu-del:disabled { opacity:.25; cursor:not-allowed; }
.tsk-swatches { display:flex; flex-wrap:wrap; gap:6px; padding:6px 8px 8px 30px; }
.tsk-swatch { width:20px; height:20px; border-radius:50%; border:2px solid var(--bg-form); box-shadow:0 0 0 1px var(--border-input); cursor:pointer; padding:0; }
.tsk-swatch:hover { transform:scale(1.12); }
.tsk-swatch.on { box-shadow:0 0 0 2px var(--color-info); }
.tsk-menu-sep { height:1px; background:var(--border-input); margin:6px 0; }
.tsk-menu-add { display:flex; gap:6px; padding:2px 4px; }
.tsk-menu-add input { flex:1; min-width:0; background:var(--bg-input); border:1px solid var(--border-input); color:var(--text-color);
  border-radius:var(--radius-input); padding:7px 9px; font-size:13px; outline:none; }
.tsk-menu-add button { border:none; background:var(--color-info); color:#fff; border-radius:var(--radius-input); padding:7px 12px; font-size:12px; font-weight:700; cursor:pointer; }

@media (max-width:1100px){ .tsk-board{grid-template-columns:repeat(2,1fr);} }
@media (max-width:640px){ .tsk-board{grid-template-columns:1fr;} }
`;

export default TasksPage;
